import { FormControl, FormGroup } from '@angular/forms';
import { merge, Subscription } from 'rxjs';
import type { Form } from '../interfaces/form.interface';
import { InputType } from '../interfaces/input.interface';
import { ReactiveFormValueUtils } from './reactive-form-value.utils';

export class ReactiveTwoFieldsConcatConnector<T> {
    private subscriptions = new Subscription();

    public bind(form: Form<T>, model: FormGroup): void {
        this.destroy();
        this.subscriptions = new Subscription();

        for (const section of form.sections) {
            if (!section.isVisible) {
                continue;
            }

            for (const field of section.fields) {
                if (!field.conditions.isVisible || field.conditions.isRetained) {
                    continue;
                }

                if (field.input?.type !== InputType.TWO_FIELDS_CONCAT) {
                    continue;
                }

                this.bindField(
                    model,
                    field.name ?? '',
                    field.input.twoFieldsConcatConfiguration?.subField1 ?? '',
                    field.input.twoFieldsConcatConfiguration?.subField2 ?? '',
                    field.input.twoFieldsConcatConfiguration?.separator ?? 'x',
                );
            }
        }
    }

    public destroy(): void {
        this.subscriptions.unsubscribe();
    }

    private bindField(model: FormGroup, parentName: string, subField1: string, subField2: string, separator: string): void {
        if (!parentName || !subField1 || !subField2) {
            return;
        }

        const parentControl = model.get(parentName) as FormControl | null;
        const subField1Control = model.get(subField1) as FormControl | null;
        const subField2Control = model.get(subField2) as FormControl | null;

        if (!parentControl || !subField1Control || !subField2Control) {
            return;
        }

        this.patchInitialSubFields(parentControl, subField1Control, subField2Control, separator);

        this.subscriptions.add(
            merge(subField1Control.valueChanges, subField2Control.valueChanges).subscribe(() => {
                const firstValue = String(subField1Control.value ?? '').trim();
                const secondValue = String(subField2Control.value ?? '').trim();
                const combinedValue = firstValue.length === 0 && secondValue.length === 0 ? '' : `${firstValue}${separator}${secondValue}`;

                if (String(parentControl.value ?? '') !== combinedValue) {
                    parentControl.patchValue(combinedValue, { emitEvent: true });
                }
            }),
        );
    }

    private patchInitialSubFields(
        parentControl: FormControl,
        subField1Control: FormControl,
        subField2Control: FormControl,
        separator: string,
    ): void {
        const initialValue = String(parentControl.value ?? '');

        const hasSubFieldValues = String(subField1Control.value ?? '').length > 0 || String(subField2Control.value ?? '').length > 0;

        if (initialValue.length === 0 || hasSubFieldValues) {
            return;
        }

        const [subField1Value, subField2Value] = ReactiveFormValueUtils.splitTwoFieldsConcat(initialValue, separator);

        subField1Control.patchValue(subField1Value, { emitEvent: false });
        subField2Control.patchValue(subField2Value, { emitEvent: false });
    }
}
